/**
 * PATH: packages/email-hub/src/feature/components/EmailSearchCheatSheet.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-EMAILSEARCHCHEATSHEET-001
 * PURPOSE: "?" affordance next to the inbox search input that opens a
 *          popover listing every supported search operator with a
 *          one-line example + description (G6 ? search operators must
 *          be discoverable without focusing the input first).
 * WHY:     The <EmailSearchSuggestions> popover only appears while
 *          the input is FOCUSED ? a user scanning the toolbar has no
 *          way to learn the vocabulary exists. The cheat sheet is the
 *          always-visible entry point; both surfaces render from the
 *          same EMAIL_SEARCH_OPERATORS dictionary so they can never
 *          drift.
 * FLOW:
 *   ????????????????   ?????????????????????????   ????????????????
 *   ? "?" button   ? ? ? EMAIL_SEARCH_OPERATORS ? ? ? popover rows ?
 *   ? (open state) ?   ? (single source)        ?   ? label+example?
 *   ????????????????   ?????????????????????????   ????????????????
 * DEPENDENCIES:
 *   - parseEmailSearchQuery: EMAIL_SEARCH_OPERATORS dictionary.
 * RELATED:
 *   - EmailSearchSuggestions.tsx: the focused-input sibling popover ?
 *     same visual conventions (border, bg-popover, z-30, text-xs).
 *   - pages/InboxPage.tsx: hosts this next to the search input.
 */

import { useEffect, useRef, useState } from 'react'
import { HelpCircle } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { EMAIL_SEARCH_OPERATORS } from '../lib/parseEmailSearchQuery'

export default function EmailSearchCheatSheet() {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click / Escape ? same lightweight idiom the
  // package's other manual popovers use (no Radix dependency here).
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const entries = Object.entries(EMAIL_SEARCH_OPERATORS) as Array<
    [string, { label: string; example: string; description: string }]
  >

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Search operator help"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Search operators (from:, is:, has:, ?)"
        className={cn(
          'h-6 w-6 rounded-md flex items-center justify-center border',
          // Solid ink on a real surface — muted-only glyphs vanish on the
          // command-bar / popover backgrounds (operator: "? is transparent").
          'border-border bg-background text-foreground',
          'hover:bg-accent hover:text-foreground',
          'transition-colors',
          open && 'bg-accent border-primary/40 text-foreground',
        )}
      >
        <HelpCircle className="h-3.5 w-3.5 shrink-0" aria-hidden strokeWidth={2.25} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Search operators"
          className={cn(
            'absolute top-full right-0 z-30 mt-1 w-72',
            'rounded-md border border-border bg-popover text-popover-foreground',
            'shadow-lg overflow-hidden',
            'animate-in fade-in-0 zoom-in-95 duration-100',
          )}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
            Search operators
          </div>
          <ul className="py-1 max-h-80 overflow-y-auto">
            {entries.map(([key, def]) => (
              <li key={key} className="px-3 py-1.5">
                <div className="flex items-baseline gap-2">
                  <code className="font-mono font-medium text-xs text-primary shrink-0">
                    {def.example}
                  </code>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {def.description}
                </div>
              </li>
            ))}
          </ul>
          <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border bg-muted/40">
            Combine operators with free text ? e.g.{' '}
            <code className="font-mono">from:mark has:attachment invoice</code>.
            Quote multi-word values: <code className="font-mono">from:"mark newman"</code>.
          </div>
        </div>
      )}
    </div>
  )
}
